import { useSearchParams } from "@solidjs/router";
import { createForm } from "@tanstack/solid-form";
import { FiAlertCircle, FiCheckCircle } from "solid-icons/fi";
import { IoSparkles } from "solid-icons/io";
import {
  createEffect,
  createMemo,
  createSignal,
  For,
  onMount,
  Show,
} from "solid-js";
import { produce } from "solid-js/store";
import { toast } from "solid-sonner";
import z from "zod";
import { useI18n } from "@/api/i18n";
import { FileInput } from "@/components/shared/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  FieldContainer,
  FieldContainerLabel,
  FieldInfo,
} from "@/components/ui/input";
import type { ProficiencyLevel } from "@/constants/languages";
import { UPLOAD_RULES } from "@/domains/documents/constants/upload";
import { getAllowedTypesLabel } from "@/domains/documents/utils/file-validation";
import {
  getUploadApiError,
  mapUploadErrorMessage,
} from "@/domains/documents/utils/upload-error";
import { usePostHog } from "@/hooks/usePostHog";
import { parseDateOrNull } from "@/libs/date";
import { areAllValuesFalsy } from "@/libs/global";
import { boundSkillTerms } from "@/libs/skill-input";
import type { CVAnalysis } from "../../api";
import { useOnboardByResume } from "../../api";
import profilePic from "../../assets/profile-pic.png";
import { createDefaultSector } from "../../constants";
import { useOnboardingStore } from "../../context/OnboardingContext";
import { toStoredUrl } from "../../utils/online-presence";
import { sectorFromProposal } from "../../utils/taxonomy-proposal";
import { OnboardingDescription, OnboardingTitle } from "../shared/text";
import { sortEducationByStartDate, sortExperienceByStartDate } from "../utils";
import { LinkedInUrlInput } from "./LinkedInUrlInput";

const HARD_CODED_SPOKEN_LANGUAGES = [
  {
    code: "en",
    name: "English",
    level: "Professionale" as ProficiencyLevel,
  },
  {
    code: "it",
    name: "Italian",
    level: "Madrelingua" as ProficiencyLevel,
  },
];

const resumeModalSchema = z.object({
  resume: z.instanceof(File),
});

type LoadingPoint = {
  text: string;
  delay: number;
};

const ResumeParsingLoadingFallback = () => {
  const { t } = useI18n();
  const [visiblePoints, setVisiblePoints] = createSignal<number[]>([]);

  const loadingPoints: LoadingPoint[] = [
    { text: t("onboarding.steps.source.curriculum.loading.point1"), delay: 0 },
    {
      text: t("onboarding.steps.source.curriculum.loading.point2"),
      delay: 400,
    },
    {
      text: t("onboarding.steps.source.curriculum.loading.point3"),
      delay: 800,
    },
    {
      text: t("onboarding.steps.source.curriculum.loading.point4"),
      delay: 1200,
    },
  ];

  onMount(() => {
    loadingPoints.forEach((_, index) => {
      setTimeout(() => {
        setVisiblePoints((prev) => [...prev, index]);
      }, loadingPoints[index].delay);
    });
  });

  return (
    <div class="flex flex-col items-center justify-center min-h-[400px] w-full py-8">
      {/* Icon/Animation */}
      <div class="mb-8 relative">
        <div class="w-20 h-20 rounded-full bg-linear-to-br from-primary/20 to-primary/5 flex items-center justify-center relative">
          <div class="absolute inset-0 rounded-full border-2 border-primary/30 animate-ping" />
          <div class="absolute inset-2 rounded-full border-2 border-primary/50 animate-pulse" />
          <div class="relative z-10 w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
            <i class="ph ph-file-text text-primary text-2xl" />
          </div>
        </div>
      </div>

      {/* Title */}
      <h3 class="text-2xl font-bold text-gray-900 mb-2 text-center">
        {t("onboarding.steps.source.curriculum.loading.title")}
      </h3>

      {/* Loading Points */}
      <div class="flex flex-col gap-4 w-full max-w-md">
        <For each={loadingPoints}>
          {(point, index) => {
            const isVisible = () => visiblePoints().includes(index());
            return (
              <div
                class="flex items-start gap-3 transition-[opacity,translate] duration-500 ease-out"
                classList={{
                  "opacity-0 -translate-x-4": !isVisible(),
                  "opacity-100 translate-x-0": isVisible(),
                }}
                style={{
                  "transition-delay": `${point.delay}ms`,
                }}
              >
                <div class="shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                  <div
                    class="w-2 h-2 rounded-full bg-primary"
                    classList={{
                      "animate-pulse": isVisible(),
                    }}
                  />
                </div>
                <p class="text-sm text-gray-700 flex-1 leading-relaxed">
                  {point.text}
                </p>
              </div>
            );
          }}
        </For>
      </div>

      {/* Spinner at the bottom */}
      <div class="mt-8 flex items-center gap-2 text-sm text-gray-500">
        <div class="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-primary" />
        <span>{t("onboarding.steps.source.curriculum.loading.wait")}</span>
      </div>
    </div>
  );
};

const ResumeDialog = () => {
  const [store, setStore] = useOnboardingStore();
  const [isEmpty, setIsEmpty] = createSignal(false);
  const { t } = useI18n();
  const onboardByResume = useOnboardByResume();
  const { capture } = usePostHog();

  const form = createForm(() => ({
    defaultValues: {
      resume: undefined as File | undefined,
    },
    schema: resumeModalSchema,
    onSubmit: async ({ value }) => {
      const validatedData = resumeModalSchema.safeParse(value);
      if (!validatedData.success) {
        console.error("Validation error", validatedData.error);
        return;
      }
      capture("start_parse_resume");
      onboardByResume.mutate({ cv: validatedData.data.resume });
      setStore(
        produce((state) => {
          state.info.value.resume = validatedData.data.resume;
        }),
      );
    },
  }));

  createEffect(() => {
    if (onboardByResume.isSuccess) {
      capture("end_parse_resume");
      const analysis = onboardByResume.data?.analysis;
      if (analysis) {
        if (areAllValuesFalsy(analysis)) {
          setIsEmpty(true);
        } else {
          setIsEmpty(false);
          setStore(
            produce((state) => {
              // Update basic info
              state.info.value = {
                ...state.info.value,
                date_of_birth: parseDateOrNull(analysis.birthdate),
                residency: analysis.residency || "",
              };

              state.skills.value = boundSkillTerms(analysis.skills || []);

              // Update job title
              state.job_title.value = analysis.job_title || "";

              const experience = analysis.experience.map((exp) => ({
                temp_id: crypto.randomUUID(),
                company: exp.company,
                company_name: exp.company,
                position: exp.position,
                job_title: exp.position,
                start_date: parseDateOrNull(exp.start_date),
                end_date: parseDateOrNull(exp.end_date),
                description: exp.description,
                is_current: exp.end_date === null,
              }));

              state.experience.value = sortExperienceByStartDate(experience);

              const education = analysis.education.map((edu) => ({
                temp_id: crypto.randomUUID(),
                institution: edu.school,
                degree: edu.degree,
                start_date: parseDateOrNull(edu.start_date),
                end_date: parseDateOrNull(edu.end_date),
                description: edu.description,
              }));
              state.education.value = sortEducationByStartDate(education);

              // Update summary
              state.summary.value = analysis.bio || "";

              // Update pricing if available
              if (analysis.price_per_day) {
                state.pricing.value.price_per_day = analysis.price_per_day;
              }

              if (HARD_CODED_SPOKEN_LANGUAGES.length > 0) {
                state.locale.value.spoken_languages =
                  HARD_CODED_SPOKEN_LANGUAGES;
              }

              // Presenza online: the analyzer is prompted for these (unlike the
              // LinkedIn scraper, which cannot see the contact-info panel), so
              // carry them over instead of asking for them again at step 10.
              // Values already in the persisted store always win.
              const presence = state.online_presence.value;
              presence.website ||= toStoredUrl(analysis.website);
              presence.portfolio ||= toStoredUrl(analysis.portfolio);
              presence.linkedin ||= toStoredUrl(analysis.socialMedia?.linkedin);
              presence.github ||= toStoredUrl(analysis.socialMedia?.github);

              // Settore e ambiti proposti dall'analisi (AC17): lo step li
              // mostra come suggerimenti da confermare.
              state.sector.value = sectorFromProposal(analysis);
              state.sector.source = "cv";
              state.sector.reviewed = false;
              state.sector.error = null;

              // Move to next step
              state.step = "sector";
              state.source.value = "resume";
            }),
          );
        }
      }
    }
  });

  createEffect(() => {
    if (onboardByResume.isError) {
      capture("fail_parse_resume");
      console.error(onboardByResume.error);
    }
  });

  // Visible, mapped error for the step (AC 11): 413/415 → code-mapped message,
  // anything else → generic onboarding copy. The FileInput below stays visible so the
  // user can retry in the same dialog without closing it.
  const uploadErrorMessage = createMemo(() => {
    if (!onboardByResume.isError) return null;
    const uploadError = getUploadApiError(onboardByResume.error);
    return uploadError
      ? mapUploadErrorMessage(uploadError.code, t, {
          allowedTypesLabel: getAllowedTypesLabel(UPLOAD_RULES.cv),
        })
      : t("common.upload_errors.onboarding_error_description");
  });

  const handleFileChange = (event: Event) => {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      form.setFieldValue("resume", file);
    }
  };

  const handleRemoveResume = () => {
    form.setFieldValue("resume", undefined);
  };

  return (
    <Dialog>
      <Card
        class="relative flex flex-col h-full rounded-lg border transition-colors hover:border-primary/50"
        classList={{
          "border-primary": store.source.value === "resume",
          "border-gray-200 bg-white": store.source.value !== "resume",
        }}
      >
        <CardContent class="p-4 flex flex-col gap-4 flex-1">
          <div class="flex items-start gap-3">
            <div class="shrink-0 w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
              <i class="ph ph-file-text text-gray-600 text-xl" />
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-1">
                <h3 class="text-base font-semibold text-gray-900">
                  {t("onboarding.steps.source.buttons.resume")}
                </h3>
                <Badge variant="light-green" size="sm">
                  <IoSparkles class="w-4 h-4 mr-1" />
                  AI
                </Badge>
              </div>
              <p class="text-sm text-gray-600">
                Carica il tuo CV in formato PDF
              </p>
            </div>
          </div>
          <div class="mt-auto">
            {/* The button is the trigger, and its `::after` covers the card,
                so a click anywhere on the card still opens the dialog without
                the card being a second control around the button. `scale`
                stays `none` when pressed: any other value, `1` included, makes
                the button the box of its `::after`, which then shrinks to the
                button under the pointer mid-click, and the click lands on the
                card instead. */}
            <DialogTrigger
              as={Button}
              variant="outline"
              class="w-full border-gray-200 bg-white hover:bg-gray-50 active:scale-none after:absolute after:inset-0 after:rounded-lg"
              onClick={() => {
                capture("click_source_resume");
              }}
            >
              Carica Curriculum
            </DialogTrigger>
          </div>
        </CardContent>
      </Card>
      <DialogContent class="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {t("onboarding.steps.source.curriculum.title")}
          </DialogTitle>
          <DialogDescription>
            {t("onboarding.steps.source.curriculum.step1")}
          </DialogDescription>
        </DialogHeader>

        <Show
          when={onboardByResume.isPending}
          fallback={
            <div>
              <Show when={uploadErrorMessage()}>
                {(message) => (
                  <Alert variant="destructive" class="mb-4">
                    <FiAlertCircle size={16} />
                    <AlertTitle>
                      {t("common.upload_errors.onboarding_error_title")}
                    </AlertTitle>
                    <AlertDescription>{message()}</AlertDescription>
                  </Alert>
                )}
              </Show>
              <form.Field
                name="resume"
                validators={{
                  onChange: resumeModalSchema.shape.resume,
                }}
              >
                {(field) => (
                  <FieldContainer>
                    <FieldContainerLabel for="resume">
                      {t("common.file_upload.upload")}
                    </FieldContainerLabel>
                    <FileInput
                      fieldApi={field()}
                      onFileChange={handleFileChange}
                      onRemoveFile={handleRemoveResume}
                      uploadRules={UPLOAD_RULES.cv}
                    />
                    <FieldInfo field={field()} />
                  </FieldContainer>
                )}
              </form.Field>
              <div class="mt-4 flex items-center justify-between gap-2">
                <DialogClose>
                  <Button variant="secondary" class="w-fit">
                    {t("common.close")}
                  </Button>
                </DialogClose>
                <form.Subscribe
                  selector={(state) => ({
                    isValid: state.values.resume !== undefined,
                  })}
                >
                  {(state) => (
                    <Button
                      onClick={() => form.handleSubmit()}
                      disabled={!state().isValid}
                    >
                      {t("onboarding.steps.source.linkedin.import")}
                    </Button>
                  )}
                </form.Subscribe>
              </div>
            </div>
          }
        >
          <ResumeParsingLoadingFallback />
        </Show>

        <Show when={isEmpty()}>
          <p class="text-sm text-red-500">
            {t("onboarding.steps.source.curriculum.empty")}
          </p>
        </Show>
      </DialogContent>
    </Dialog>
  );
};

const LinkedInCardButton = (props: { autoOpen?: boolean }) => {
  const [store, setStore] = useOnboardingStore();
  const { t } = useI18n();
  const handleLinkedInSuccess = (analysis: CVAnalysis, linkedinUrl: string) => {
    setStore(
      produce((state) => {
        // Update profile full_name with LinkedIn data
        if (analysis.full_name) {
          state.profile.full_name = analysis.full_name;
        }

        // Update basic info
        state.info.value = {
          ...state.info.value,
          residency: analysis.residency || "",
          date_of_birth: parseDateOrNull(analysis.birthdate),
          avatar_url: analysis.avatar_url || "",
        };

        state.skills.value = boundSkillTerms(analysis.skills || []);

        // Update job title
        state.job_title.value = analysis.job_title || "";

        const experience = analysis.experience.map((exp) => ({
          temp_id: crypto.randomUUID(),
          company: exp.company,
          company_name: exp.company,
          position: exp.position,
          job_title: exp.position,
          start_date: parseDateOrNull(exp.start_date),
          end_date: parseDateOrNull(exp.end_date),
          description: exp.description,
          is_current: exp.end_date === null,
        }));

        // Update experience
        state.experience.value = sortExperienceByStartDate(experience);

        const education = analysis.education.map((edu) => ({
          temp_id: crypto.randomUUID(),
          institution: edu.school,
          degree: edu.degree,
          start_date: parseDateOrNull(edu.start_date),
          end_date: parseDateOrNull(edu.end_date),
          description: edu.description,
        }));

        // Update education
        state.education.value = sortEducationByStartDate(education);

        // Update summary
        state.summary.value = analysis.bio || "";

        // Update pricing if available
        if (analysis.price_per_day) {
          state.pricing.value.price_per_day = analysis.price_per_day;
        }

        if (HARD_CODED_SPOKEN_LANGUAGES.length > 0) {
          state.locale.value.spoken_languages = HARD_CODED_SPOKEN_LANGUAGES;
        }

        // Presenza online: the profile we just imported is itself a LinkedIn
        // link the user should not have to retype. Prefer the canonical URL the
        // scraper echoes back, fall back to the one that was submitted, and
        // never overwrite a value already in the (persisted) store.
        state.online_presence.value.linkedin ||= toStoredUrl(
          analysis.socialMedia?.linkedin || linkedinUrl,
        );

        // Settore e ambiti proposti dall'analisi (AC17).
        state.sector.value = sectorFromProposal(analysis);
        state.sector.source = "linkedin";
        state.sector.reviewed = false;
        state.sector.error = null;

        // Move to next step
        state.step = "sector";
        state.source.value = "linkedin_apify";
      }),
    );
  };

  const handleLinkedInError = (error: string) => {
    toast.error(error || t("onboarding.steps.source.linkedin.error"));
  };

  const isSelected = () =>
    store.source.value === "linkedin_apify" ||
    store.source.value === "linkedin";

  return (
    <LinkedInUrlInput
      onSuccess={handleLinkedInSuccess}
      onError={handleLinkedInError}
      isSelected={isSelected()}
      buttonLabel="Inizia con LinkedIn"
      buttonVariant="default"
      showButtonIcon={true}
      autoOpen={props.autoOpen}
    />
  );
};

const LinkedInCard = (props: { autoOpen?: boolean }) => {
  const [store] = useOnboardingStore();
  const { t } = useI18n();

  const isSelected = () =>
    store.source.value === "linkedin_apify" ||
    store.source.value === "linkedin";

  return (
    <Card
      class="relative rounded-lg border-2 transition-colors hover:border-primary/50"
      classList={{
        "border-primary": isSelected(),
        "border-primary/30": !isSelected(),
      }}
    >
      <CardContent class="p-6">
        {/* Recommended Badge */}
        <div class="absolute top-4 right-4 hidden sm:block">
          <Badge
            variant="default"
            size="sm"
            class="flex items-center gap-1 bg-primary text-white"
          >
            <span class="text-xs">Consigliato</span>
          </Badge>
        </div>

        <div class="flex flex-col gap-6">
          {/* Header with Logo */}
          <div class="flex items-start gap-4">
            <div class="p-3 bg-indigo-500 rounded-lg">
              <svg
                class="w-5 sm:w-8 h-5 sm:h-8 text-white"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-label="LinkedIn Logo"
              >
                <title>LinkedIn Logo</title>
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
            </div>

            <div class="flex-1">
              <div class="flex items-center gap-2 mb-1">
                <h3 class="text-xl font-semibold text-gray-900">
                  {t("onboarding.steps.source.buttons.linkedin")}
                </h3>
                <Badge variant="light-green" size="sm">
                  <IoSparkles class="w-4 h-4 mr-1" />
                  AI
                </Badge>
              </div>
              <p class="text-sm text-gray-600">
                Il modo più veloce e preciso per creare il tuo profilo. Importa
                automaticamente tutte le tue esperienze e competenze.
              </p>
            </div>
          </div>

          {/* Benefits List */}
          <div class="flex flex-col gap-3">
            <div class="flex items-center gap-2">
              <FiCheckCircle class="w-4 h-4 text-primary" />
              <span class="text-sm text-gray-700">
                Completamento automatico in 2 minuti
              </span>
            </div>
            <div class="flex items-center gap-2">
              <FiCheckCircle class="w-4 h-4 text-primary" />
              <span class="text-sm text-gray-700">
                Include competenze ed esperienze lavorative
              </span>
            </div>
            <div class="flex items-center gap-2">
              <FiCheckCircle class="w-4 h-4 text-primary" />
              <span class="text-sm text-gray-700">
                Aggiorna il profilo con 1 click
              </span>
            </div>
          </div>

          {/* CTA Button */}
          <div class="pt-2 w-full">
            <LinkedInCardButton autoOpen={props.autoOpen} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Source = () => {
  const [store, setStore] = useOnboardingStore();
  const [searchParams] = useSearchParams();
  const { t } = useI18n();
  const { capture } = usePostHog();

  // Se c'è il parametro source=linkedin_apify, apri automaticamente il dialog LinkedIn
  const autoOpenLinkedIn = () => {
    const source = searchParams.source;
    return source === "linkedin_apify";
  };

  onMount(() => {
    capture("start_onboarding");
  });
  return (
    <div class="flex flex-col gap-8 w-full">
      <div class="flex flex-col gap-4">
        <OnboardingTitle>{t("onboarding.steps.source.title")}</OnboardingTitle>
        <OnboardingDescription>
          {t("onboarding.steps.source.description")}
        </OnboardingDescription>
      </div>
      <div class="flex gap-6 justify-between">
        <div class="flex flex-col gap-4 w-full md:w-2/3">
          {/* LinkedIn Card - Large, Prominent */}
          <LinkedInCard autoOpen={autoOpenLinkedIn()} />

          {/* Bottom Row: Resume and Manual Cards */}
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Resume Card */}
            <ResumeDialog />

            {/* Manual Card */}
            <Card
              class="flex-1 flex flex-col rounded-lg border transition-colors hover:border-primary/50"
              classList={{
                "border-primary": store.source.value === "manual",
                "border-gray-200 bg-white": store.source.value !== "manual",
              }}
            >
              <CardContent class="p-4 flex flex-col gap-4 flex-1">
                <div class="flex items-start gap-3">
                  <div class="shrink-0 w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                    <i class="ph ph-clock text-gray-600 text-xl" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <h3 class="text-base font-semibold text-gray-900 mb-1">
                      {t("onboarding.steps.source.buttons.manual")}
                    </h3>
                    <div class="text-sm text-gray-600">
                      <p>Inserisci manualmente le tue informazioni</p>
                      <p>~15 minuti</p>
                    </div>
                  </div>
                </div>
                <div class="mt-auto">
                  <Button
                    variant="outline"
                    class="w-full border-gray-200 bg-white hover:bg-gray-50"
                    onClick={() => {
                      setStore(
                        produce((state) => {
                          // Da zero: nessuna proposta, nessuna legenda (AC17).
                          state.sector = createDefaultSector();
                          state.step = "sector";
                          state.source.value = "manual";
                        }),
                      );
                      capture("click_source_manual");
                    }}
                  >
                    Inizia da zero
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Right Sidebar - Keep as is */}
        <Card class="hidden md:block w-1/3 rounded-lg shadow-none bg-primary/5 max-h-fit">
          <CardContent class="p-4">
            <div class="flex flex-col gap-2">
              <img src={profilePic} alt="LinkedIn" width={100} />
              <p class="text-xl text-primary">
                {t("onboarding.steps.source.card.description")}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Source;
